import React, { useEffect, useState, useMemo } from "react";
import { createTheme, ThemeProvider } from '@material-ui/core/styles';

import { Container, Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import MaterialTable from "material-table";
import moment from "moment-timezone";
import { msa_additional_info_cols, msa_price_input_cols } from "../../../util/columns";
import { useUser } from "../../../util/user";
import GYAxios from "../../../util/GYAxios";
import Noti from "../../../util/noti";

import LoaderGraph from "../../general/LoaderGraph";
import "react-datepicker/dist/react-datepicker.css";
import { localTimeToPickerTime, pickerTimeTolocalTime } from "../../../util/momentTimezone";
const lastMonth = moment().subtract(1, "month").toDate();

const theme = createTheme({
    overrides: {
        MuiFormHelperText: {
            root: {
                fontSize: '1.3em',
            },
        },
    },
});

function InputMSA() {
    const [tableState, setTableState] = useState(false); // Table is ready or not
    const [data, setData] = useState([]); // Data for table
    const [title, setTitle] = useState(""); // Title of table
    const [datePicker, setDatePicker] = useState(lastMonth); // Date of date-picker
    const [minDate, setMinDate] = useState(null); // Min date of date-picker
    const [maxDate, setMaxDate] = useState(lastMonth); // Max date of date-picker
    const user = useUser(); // Logged-in user
    let show_unit_price = false;

    const msacols = useMemo(() => {
        // if datePicker has been greater than 2024-09, then return msa_price_input_cols
        if (moment(datePicker).isAfter("2024-09-01 00:00:00")) {
            show_unit_price = true;
            return msa_price_input_cols;
        } else {
            return msa_additional_info_cols;
        }
    }, [datePicker]);

    /**
     * Get list of sites that their previous-month invoices being approved
     * @param {object} datePicked would be empty when first loading
     * and would be updated when Users change month-year in date-picker
     */
    async function extractData(datePicked) {
        await GYAxios.get(`/invoice/msa_inputs?datePicked=${datePicked}`)
            .then((res) => {
                if (res.status === 200) {
                    let array = [];
                    let string = `MSA Invoice in ${moment(localTimeToPickerTime(datePicker)).format("MMMM YYYY")}`;
                    if (res.data.length > 0) {
                        res.data.forEach((i) => {
                            const item = {
                                id: i.id,
                                client: i.company,
                                site: i.site_name,
                                tableName: i.table_name,
                                invMon: i.ref_monthData,
                                msa: moment(i.msa).format("YYYY-MM-DD"),
                                totalDays: i.msa_day,
                                note: i.msa_description,
                                updatedID: i.updatedBy,
                                updatedBy: i.updatedBy,
                                updatedAt: i.updatedAt,
                                approvalStatus: i.approvalStatus,
                            };
                            show_unit_price && (item.unitPrice = i.unit_price);
                            array.push(item);
                        });
                    }
                    setData(array);
                    setTitle(string);
                    setTableState(true);
                } else {
                    Noti.create({
                        type: "e",
                        msg: "Something went wrong!"
                    });
                    return;
                }
            })
            .catch(() => {
                Noti.create({
                    type: "e",
                    msg: "Something went wrong!"
                });
                return;
            });
    }

    /**
     * Validate the input from user
     * @param {String} input value of input from cell that user modifies
     * @returns boolean
     */
    function validate(input) {
        let isValid = false;
        switch (input) {
            case "Invalid date":
                isValid = false;
                break;

            case "NaN":
                isValid = false;
                break;

            default:
                isValid = true;
                break;
        }

        return isValid;
    }

    /**
     * Validate the updated end date
     * @param {Object} data merged data of old and new data of selected row
     * @returns boolean
     */
    /* function isValidObj(data) {
        let isValid = true;
        if (!data.totalDays) {
            isValid = false;
        }
        return isValid;
    } */

    /**
     * Update data of selected row to server database
     * and set data for table again (with updated values)
     * @param {Object} newData updated data of selected row
     * @param {Object} oldData old data of selected row
     * @param {Function} resolve after Axios function
     * -> reload the table value from front-end
     */
    async function updateData(newData, oldData, resolve, reject) {
        const modified_data_array = Object.entries(newData).filter(([key, value]) => value !== oldData[key]);
        const modified_data = Object.fromEntries(modified_data_array);

        let isOK = true;
        for (let key in modified_data) {
            if (!validate(modified_data[key])) {
                isOK = false;
                break;
            }
        }

        if (isOK) {
            if (Object.keys(modified_data).length === 0) resolve();
            else {
                await GYAxios.put("/invoice/update_msa_input", { ...modified_data, id: oldData.id, updatedBy: user.email }).then((res) => {
                    if (res.status === 200 && res.data.affectedRows === 1 && res.data.changedRows === 1) {
                        const dataUpdate = [...data];
                        const index = oldData.tableData.id;
                        dataUpdate[index] = { ...oldData, ...modified_data, id: oldData.id, updatedBy: user.email };
                        setData([...dataUpdate]);
                        Noti.create({
                            type: "s",
                            msg: "Updated!",
                        });
                        resolve();
                    } else {
                        Noti.create({
                            type: "w",
                            msg: "Nothing is updated",
                        });
                        resolve();
                    }
                }).catch((err) => {
                    Noti.create({
                        type: "e",
                        msg: "Something went wrong!",
                    });
                    reject();
                });
            }

        } else {
            Noti.create({
                type: "e",
                msg: "You have filled invalid data!"
            });
            reject();
        }
    }

    /**
     * Update data of table and date selected of date-picker
     * @param {Date} date that User changed
     */
    /* function onChangeDatePicker(date) {
        setTableState(false);
        setData([]);
        setTimeout(() => {
            setDatePicker(date);
            extractData({ date: moment(date).format("YYYY-MM-DD") });
        }, 1000);
    } */

    useEffect(() => {
        // first loading, get min date of date-picker and extract data from last month
        GYAxios.get("/invoice/msa_ref_dates").then((res) => {
            if (res.status === 200) {
                if (res.data.length !== 0) {
                    setMinDate(moment(res.data[0].mindate).toDate());
                    extractData(moment(datePicker).format("YYYY-MM-DD"));
                }
            }
        });
    }, []);

    useEffect(() => {
        // when date-picker is changed, extract data from new date
        const timeout = setTimeout(() => {
            extractData(moment(datePicker).format("YYYY-MM-DD"));
        }, 1000);

        if (datePicker !== null) {
            setTableState(false);
            setData([]);
            return () => clearTimeout(timeout);
        }
    }, [datePicker]);

    return (
        <>
            <Container fluid style={{ padding: 0 }}>
                <div className="panel-heading ">
                    <Row>
                        <Col md="6">
                            <div className="panel-title" id="date">
                                Monthly MSA Inputs
                            </div>

                            {minDate != null && (
                                <div className="month-selector">
                                    Choose another Month: &nbsp;&nbsp;
                                    <DatePicker
                                        minDate={localTimeToPickerTime(minDate)}
                                        maxDate={localTimeToPickerTime(maxDate)}
                                        selected={localTimeToPickerTime(datePicker)}
                                        onChange={(date) => setDatePicker(pickerTimeTolocalTime(date))}
                                        dateFormat="MMMM yyyy"
                                        showMonthYearPicker
                                    />
                                </div>
                            )}
                        </Col>
                    </Row>
                </div>

                {tableState ? (
                    <ThemeProvider theme={theme}>
                        <MaterialTable
                            title={title}
                            columns={msacols}
                            data={data}
                            editable={{
                                isEditable: (rowData) => rowData.approvalStatus < 2,
                                onRowUpdate: (newData, oldData) =>
                                    new Promise(async (resolve, reject) => {
                                        await updateData(newData, oldData, resolve, reject);
                                    }),
                            }}
                            options={{
                                actionsColumnIndex: -1,
                                sorting: true,
                                pageSize: 10,
                                pageSizeOptions: [10, 20, 30],
                                emptyRowsWhenPaging: false
                            }}
                            style={{ marginBottom: "10px", zIndex: 0, fontSize: "12px" }}
                            detailPanel={(rowData) => {
                                return (
                                    <ul
                                        style={{
                                            paddingLeft: "7%",
                                            margin: "10px",
                                            fontSize: "12px",
                                        }}
                                    >
                                        <li>Last updated by: {rowData.updatedBy}</li>
                                        <li>
                                            Last updated at:{" "}
                                            {moment(rowData.updatedAt).format("DD/MM/YYYY")}
                                        </li>
                                    </ul>
                                );
                            }}
                        />
                    </ThemeProvider>
                ) : (
                    <LoaderGraph />
                )}
            </Container>

            <Noti />
        </>
    );
}

export default InputMSA;
